import React from 'react'
import { useParams } from 'react-router-dom'
import InputMask from 'react-input-mask'
import { Button, Form, Input, Modal, Spin, Tooltip, Typography } from 'antd'
import { AxiosError } from 'axios'
import {
  LockOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  UserOutlined
} from '@ant-design/icons'
import { adminEmployeesApi } from 'api/adminEmployees'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import { IOpenAdminInvitationResponse } from 'models/adminEmployees'
import { TInvitationErrorCode } from 'models/sellerEmployees'
import { appActions } from 'store/app'
import { FRESH_LOGIN_STORAGE_KEY } from 'shared/constants/storage'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import {
  getContactCodeCooldownSeconds,
  setContactCodeCooldown
} from 'shared/utils/contactCodeCooldown'
import { normalizePhone } from 'shared/utils/normalizePhone'
import { passwordPattern } from 'shared/utils/patterns'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { PersonalDataConsent } from 'components/auth/PersonalDataConsent/PersonalDataConsent'
import { AuthContentWrapper } from 'components/layout/AuthContentWrapper/AuthContentWrapper'
import styles from './Invitation.module.scss'
import type { GetRef } from 'antd'

const CODE_LENGTH = 4

interface IInvitationForm {
  name?: string
  surname?: string
  password?: string
  confirm?: string
  phone?: string
  personal_data_consent?: boolean
}

type TServerError = AxiosError<{ message?: string; code?: string }>

interface IInvitationError {
  title: string
  text: string
  // сбой сети или сервера, а не сама ссылка: открыть приглашение можно заново
  canRetry?: boolean
}

// В команду магазина и в команду администраторов площадки приглашают одинаково
export type TInvitationKind = 'seller' | 'admin'

type TOpenedInvitation = IOpenAdminInvitationResponse & { shop_name?: string }

const INVITATION_KINDS = {
  seller: {
    api: sellerEmployeesApi,
    title: 'Приглашение в кабинет селлера',
    inviter: 'владельца магазина',
    getIntro: ({ shop_name }: TOpenedInvitation) =>
      `Вас пригласили в команду магазина «${shop_name}»`
  },
  admin: {
    api: adminEmployeesApi,
    title: 'Приглашение в панель администратора',
    inviter: 'супер-админа',
    getIntro: () => 'Вас пригласили администратором площадки HorseSmart'
  }
}

const getInvitationErrors = (
  inviter: string
): Record<TInvitationErrorCode, IInvitationError> => ({
  INVITATION_NOT_FOUND: {
    title: 'Приглашение недействительно',
    text: 'Ссылка устарела или уже использована. Если вы уже приняли приглашение, войдите под своей почтой.'
  },
  INVITATION_EXPIRED: {
    title: 'Срок приглашения истёк',
    text: `Ссылка действовала 7 дней. Попросите ${inviter} отправить приглашение повторно.`
  },
  INVITATION_OUTDATED: {
    title: 'Ссылка устарела',
    text: 'Приглашение отправили повторно. Перейдите по ссылке из последнего письма.'
  }
})

const getErrorMessage = (e: unknown) =>
  (e as TServerError).response?.data?.message

// Кулдаун своего запроса кода: у входа по телефону он отдельный
const getCooldownKey = (phone: string) => `invitation:${phone}`

const goToCabinet = () => window.location.replace('/')

export const Invitation: React.FC<{ kind?: TInvitationKind }> = ({
  kind = 'seller'
}) => {
  const { token = '' } = useParams<{ token: string }>()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm<IInvitationForm>()
  const { api: invitationApi, title, inviter, getIntro } =
    INVITATION_KINDS[kind]
  const [invitation, setInvitation] =
    React.useState<TOpenedInvitation | null>(null)
  const [invitationError, setInvitationError] =
    React.useState<IInvitationError | null>(null)
  // пароль из формы: на шаге кода форма уже снята
  const [credentials, setCredentials] = React.useState<IInvitationForm>({})
  const [isOpening, setIsOpening] = React.useState(true)
  const [isLoading, setIsLoading] = React.useState(false)
  // номер, на который ушёл код, и он же со скрытыми цифрами из ответа
  const [phone, setPhone] = React.useState('')
  const [maskedPhone, setMaskedPhone] = React.useState('')
  const [codeError, setCodeError] = React.useState('')
  const [timer, setTimer] = React.useState(0)
  const [codeFieldKey, setCodeFieldKey] = React.useState(0)
  const [isPhoneCheckOpen, setIsPhoneCheckOpen] = React.useState(false)
  // подтверждённый номер аккаунта решили заменить
  const [isChangingPhone, setIsChangingPhone] = React.useState(false)
  const codeRef = React.useRef<GetRef<typeof Input.OTP>>(null)
  const isCodeStep = Boolean(maskedPhone)
  const asksPhone = Boolean(invitation?.needs_phone || isChangingPhone)

  const getInvitationError = (e: unknown) => {
    const code = (e as TServerError).response?.data?.code
    const errors = getInvitationErrors(inviter)
    return code && code in errors
      ? errors[code as TInvitationErrorCode]
      : null
  }

  const open = async () => {
    try {
      setIsOpening(true)
      setInvitationError(null)
      const { data } = await invitationApi.openInvitation(token)
      setInvitation(data)
    } catch (e) {
      setInvitationError(
        getInvitationError(e) ?? {
          title: 'Не удалось открыть приглашение',
          text:
            getErrorMessage(e) ?? 'Проверьте интернет и попробуйте ещё раз.',
          canRetry: true
        }
      )
    } finally {
      setIsOpening(false)
    }
  }

  React.useEffect(() => {
    open()
  }, [token])

  React.useEffect(() => {
    if (!isCodeStep) return

    // Input.OTP не пробрасывает inputMode в ячейки: без него на телефоне
    // открывается буквенная клавиатура
    codeRef.current?.nativeElement
      .querySelectorAll('input')
      .forEach((input) => {
        input.inputMode = 'numeric'
      })
    codeRef.current?.focus()
  }, [isCodeStep, codeFieldKey])

  React.useEffect(() => {
    if (!phone) return

    const key = getCooldownKey(phone)
    setTimer(getContactCodeCooldownSeconds(key))
    const interval = setInterval(
      () => setTimer(getContactCodeCooldownSeconds(key)),
      1000
    )
    return () => clearInterval(interval)
  }, [phone])

  const accept = async (
    code?: string,
    values: IInvitationForm = credentials
  ) => {
    const { name, surname, password, confirm, personal_data_consent } = values
    try {
      setIsLoading(true)
      setCodeError('')
      const { data } = await invitationApi.acceptInvitation({
        token,
        name: invitation?.needs_name ? name : undefined,
        surname: invitation?.needs_name ? surname : undefined,
        password: invitation?.needs_password ? password : undefined,
        password_confirm: invitation?.needs_password ? confirm : undefined,
        code,
        personal_data_consent: invitation?.needs_consent
          ? personal_data_consent
          : undefined
      })
      sessionStorage.setItem(FRESH_LOGIN_STORAGE_KEY, '1')
      dispatch(appActions.setToken(data))
      // Кабинет открываем с чистой загрузки: роутеры выбираются по состоянию входа
      goToCabinet()
    } catch (e) {
      const error = getInvitationError(e)
      if (error) return setInvitationError(error)
      if (!code) return showServerError(e)

      setCodeError(getErrorMessage(e) ?? 'Неверный код подтверждения')
      setCodeFieldKey((key) => key + 1)
    } finally {
      setIsLoading(false)
    }
  }

  const requestCode = async (phoneValue: string) => {
    const normalizedPhone = String(normalizePhone(phoneValue))
    const key = getCooldownKey(normalizedPhone)
    try {
      setIsLoading(true)
      const { data } = await invitationApi.requestInvitationPhoneCode(
        token,
        normalizedPhone
      )
      setContactCodeCooldown(key, data.resend_after_sec)
      setPhone(normalizedPhone)
      setMaskedPhone(data.phone)
      setCodeError('')
      setCodeFieldKey((value) => value + 1)
      return true
    } catch (e) {
      const error = getInvitationError(e)
      if (error) {
        setInvitationError(error)
        return false
      }
      // код уже ушёл, а вкладка об этом не знает — заводим таймер по ответу
      if (getErrorMessage(e)?.includes('секунд')) {
        setContactCodeCooldown(key)
        setTimer(getContactCodeCooldownSeconds(key))
      }
      showServerError(e)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = (values: IInvitationForm) => {
    setCredentials(values)
    if (asksPhone && values.phone) return requestCode(values.phone)
    if (invitation?.phone) return setIsPhoneCheckOpen(true)
    return accept(undefined, values)
  }

  const keepPhone = () => {
    setIsPhoneCheckOpen(false)
    accept()
  }

  const replacePhone = () => {
    setIsPhoneCheckOpen(false)
    setIsChangingPhone(true)
  }

  const resendCode = async () => {
    if (await requestCode(phone)) {
      showMessage('success', 'Код отправлен повторно', '')
    }
  }

  const changePhone = () => {
    setMaskedPhone('')
    setCodeError('')
  }

  if (isOpening) {
    return <Spin size='large' />
  }

  if (invitationError || !invitation) {
    return (
      <AuthContentWrapper
        title={invitationError?.title ?? 'Приглашение недействительно'}
        titleLevel={2}
        titleClass={styles.title}
        padding='60px 66px'
      >
        <div className={styles.content}>
          <Typography.Text>{invitationError?.text}</Typography.Text>
          {invitationError?.canRetry ? (
            <Button block size='large' type='primary' onClick={open}>
              Попробовать ещё раз
            </Button>
          ) : (
            <Button block size='large' type='primary' onClick={goToCabinet}>
              Перейти ко входу
            </Button>
          )}
        </div>
      </AuthContentWrapper>
    )
  }

  const { needs_name, needs_password, needs_consent } = invitation

  return (
    <AuthContentWrapper
      title={title}
      titleLevel={2}
      titleClass={styles.title}
      padding='60px 66px'
      onGoBack={isCodeStep ? changePhone : undefined}
    >
      <div className={styles.content}>
        <Typography.Text>
          {getIntro(invitation)}
          {needs_password && !isCodeStep && (
            <>
              <br />
              {needs_name
                ? 'Укажите имя и установите пароль для входа'
                : 'Установите пароль для входа'}
            </>
          )}
        </Typography.Text>
        {isCodeStep ? (
          <>
            <Typography.Text>
              Код подтверждения отправлен по SMS на номер {maskedPhone}. Код
              действует 10 минут.
            </Typography.Text>
            <Input.OTP
              key={codeFieldKey}
              ref={codeRef}
              length={CODE_LENGTH}
              disabled={isLoading}
              status={codeError ? 'error' : undefined}
              formatter={(value) => value.replace(/\D/g, '')}
              onChange={(code) => accept(code)}
            />
            {codeError && (
              <Typography.Text type='danger'>{codeError}</Typography.Text>
            )}
            <Typography.Link
              disabled={timer > 0 || isLoading}
              onClick={resendCode}
            >
              {timer > 0
                ? `Отправить код повторно можно через ${timer} сек`
                : 'Отправить код повторно'}
            </Typography.Link>
          </>
        ) : (
          <Form
            form={form}
            name='invitation'
            size='large'
            className={styles.form}
            onFinish={onSubmit}
          >
            {needs_name && (
              <>
                <Form.Item
                  name='name'
                  rules={[
                    { required: true, whitespace: true, message: 'Укажите имя' }
                  ]}
                >
                  <Input
                    placeholder='Имя'
                    prefix={<UserOutlined className='icon' />}
                    maxLength={100}
                    autoComplete='given-name'
                  />
                </Form.Item>
                <Form.Item
                  name='surname'
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: 'Укажите фамилию'
                    }
                  ]}
                >
                  <Input
                    placeholder='Фамилия'
                    prefix={<UserOutlined className='icon' />}
                    maxLength={100}
                    autoComplete='family-name'
                  />
                </Form.Item>
              </>
            )}
            {needs_password && (
              <>
                <Form.Item
                  name='password'
                  rules={[
                    { required: true, message: 'Придумайте пароль' },
                    {
                      max: 30,
                      message: 'Пароль должен состоять максимум из 30 символов'
                    },
                    ...passwordPattern
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    placeholder='Пароль'
                    prefix={<LockOutlined className='icon' />}
                    autoComplete='new-password'
                  />
                </Form.Item>
                <Form.Item
                  name='confirm'
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Повторите пароль' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve()
                        }
                        return Promise.reject(new Error('Пароли не совпадают'))
                      }
                    })
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    placeholder='Повтор пароля'
                    prefix={<LockOutlined className='icon' />}
                    autoComplete='new-password'
                  />
                </Form.Item>
              </>
            )}
            {asksPhone && (
              <Form.Item
                name='phone'
                extra={
                  isChangingPhone && (
                    <Typography.Link onClick={() => setIsChangingPhone(false)}>
                      Оставить прежний номер {invitation.phone}
                    </Typography.Link>
                  )
                }
                rules={[
                  { required: true, message: 'Укажите номер телефона' },
                  { min: 18, message: 'Укажите номер телефона' }
                ]}
              >
                <InputMask mask='+7 (999) 999-99-99' maskChar={null}>
                  {/* @ts-ignore */}
                  {() => (
                    <Input
                      placeholder={
                        isChangingPhone
                          ? 'Новый номер телефона'
                          : 'Номер телефона'
                      }
                      prefix={<PhoneOutlined className='icon' />}
                      suffix={
                        <Tooltip title='Требуется для уведомлений'>
                          <QuestionCircleOutlined className={styles.helpIcon} />
                        </Tooltip>
                      }
                      autoComplete='tel'
                    />
                  )}
                </InputMask>
              </Form.Item>
            )}
            {needs_consent && <PersonalDataConsent subject='buyer' />}
            <Button block htmlType='submit' type='primary' loading={isLoading}>
              {asksPhone ? 'Получить код' : 'Принять приглашение'}
            </Button>
          </Form>
        )}
      </div>
      <Modal
        open={isPhoneCheckOpen}
        title='Это ваш номер телефона?'
        onCancel={() => setIsPhoneCheckOpen(false)}
        footer={[
          <Button key='change' onClick={replacePhone}>
            Изменить
          </Button>,
          <Button key='keep' type='primary' onClick={keepPhone}>
            Да, мой
          </Button>
        ]}
      >
        <Typography.Text>
          В вашем аккаунте HorseSmart подтверждён номер{' '}
          <span className={styles.phone}>{invitation.phone}</span>. По нему
          можно входить в кабинет. Если номер сменился, укажите новый — мы
          пришлём на него код.
        </Typography.Text>
      </Modal>
    </AuthContentWrapper>
  )
}
